import React from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Link } from 'react-router-dom';
import {
  Globe, Brain, Zap, MessageSquare, FileText, Video,
  Share2, Crown, Check, Sparkles, Shield, Clock, Users,
  TrendingUp, Star, ArrowRight, Building2, Briefcase, Code2, Store
} from 'lucide-react';
import { PRICING, formatPrice } from '../constants/pricing';
import { OFFER } from '../constants/demos';
import DoneForYouCallout from '../components/DoneForYouCallout';

// SEO-optimized page — keyword targets:
// "AI website builder one-time payment", "no monthly fee website builder",
// "AI website generator small business", "website live in 60 seconds"

const agents = [
  { name: 'Industry Analyst', role: 'Studies your market, competitors, and audience before writing a single word', icon: '🔍', color: '#6366f1' },
  { name: 'Copywriter', role: 'Writes headlines, body copy, and calls-to-action tailored to your industry', icon: '✍️', color: '#3b82f6' },
  { name: 'Design Director', role: 'Selects color schemes, layouts, and visual hierarchy for maximum impact', icon: '🎨', color: '#8b5cf6' },
  { name: 'SEO Auditor', role: 'Reviews every page for search engine visibility and keyword structure', icon: '📈', color: '#06b6d4' },
  { name: 'Quality Inspector', role: 'Checks for errors, broken elements, and inconsistencies throughout', icon: '🔎', color: '#10b981' },
  { name: 'Content Validator', role: 'Ensures accuracy, tone consistency, and brand voice across all sections', icon: '✅', color: '#f59e0b' },
  { name: 'Chief Reviewer', role: 'Scores the final result and approves it — or sends it back for refinement', icon: '⭐', color: '#ef4444' },
];

const competitors = [
  { name: 'Wix AI', price: '$17–$29/mo', annual: '$204–$348/yr', hasLTD: false },
  { name: 'Squarespace', price: '$16–$23/mo', annual: '$192–$276/yr', hasLTD: false },
  { name: 'Durable.co', price: '$12–$25/mo', annual: '$144–$300/yr', hasLTD: false },
  { name: '10Web', price: '$10–$24/mo', annual: '$120–$288/yr', hasLTD: false },
  { name: 'globalnow.biz Self-service PRO', price: `One-time ${formatPrice(PRICING.PRO_PRICE)}`, annual: 'No renewals. Ever.', hasLTD: true },
];

const contentTools = [
  { icon: FileText, title: 'Blog Post Generator', desc: 'SEO-optimized articles with your tone, keywords, and target audience built in. Publish-ready in under a minute.', color: 'blue' },
  { icon: Video, title: 'YouTube Script Creator', desc: 'Full video scripts with hook, body, and call-to-action — formatted for any duration and style.', color: 'red' },
  { icon: Share2, title: 'Social Media Posts', desc: 'Platform-specific posts for LinkedIn, Twitter/X, Facebook, and Instagram — one topic, four formats.', color: 'green' },
];

const useCases = [
  {
    icon: Store,
    title: 'Small Business Owners',
    desc: 'Professional website live today. No developer, no monthly agency retainer, no technical skills needed. Pay once, own it forever.',
    highlight: 'Most popular'
  },
  {
    icon: Briefcase,
    title: 'Entrepreneurs & Founders',
    desc: 'Launch your idea fast. AI handles design, copy, photos, and deploy in under 60 seconds. You stay focused on building the business.',
    highlight: null
  },
  {
    icon: Code2,
    title: 'Freelancers & Web Designers',
    desc: 'Generate client sites in minutes, not hours. Charge your full rate, deliver faster, take on more clients. One lifetime license covers every project.',
    highlight: 'High value'
  },
  {
    icon: Building2,
    title: 'Agencies',
    desc: 'Scale website production with 7-agent quality review built in. Every output is reviewed before your client sees it — automatically.',
    highlight: null
  },
];

const Features = () => {
  return (
    <div className="min-h-screen py-12 px-6">
      <div className="max-w-7xl mx-auto">
        <DoneForYouCallout />

        {/* ── HERO ── */}
        <div className="text-center mb-20">
          <Badge className="bg-blue-500/20 text-blue-400 mb-6 px-4 py-1.5 text-sm">
            <Sparkles className="w-3 h-3 mr-2" />
            Self-service tools — separate from done-for-you websites
          </Badge>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            Build Your Own Website,
            <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-blue-400 bg-clip-text text-transparent"> with Self-service Tools</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-4 leading-relaxed">
            Use the generator and content tools to build and manage a site yourself. These are the self-service product features.
          </p>
          <p className="text-lg font-semibold text-blue-300 mb-10">
            Prefer us to handle the build? Our ${OFFER.price} done-for-you website starts with a free sample above.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/website-generator">
              <Button size="lg" className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-lg px-8 py-6">
                <Globe className="w-5 h-5 mr-2" />
                Open Self-service Builder
              </Button>
            </Link>
            <Link to="/upgrade">
              <Button size="lg" variant="outline" className="border-gray-600 text-white hover:bg-gray-800 text-lg px-8 py-6">
                <Crown className="w-5 h-5 mr-2" />
                Self-service PRO Pricing
              </Button>
            </Link>
          </div>
        </div>

        {/* ── COMPETITOR COMPARISON ── */}
        <div className="mb-24">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-white mb-3">Self-service Builder Pricing Comparison</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">This comparison is for self-service tools. PRO gives you lifetime access to those tools; the done-for-you website is a separate order.</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full max-w-3xl mx-auto">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Platform</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Monthly Cost</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Annual Cost</th>
                  <th className="text-center py-3 px-4 text-gray-400 font-medium">One-Time Option</th>
                </tr>
              </thead>
              <tbody>
                {competitors.map((c, i) => (
                  <tr key={i} className={`border-b border-gray-800 ${c.hasLTD ? 'bg-blue-950/40' : ''}`}>
                    <td className="py-4 px-4">
                      <span className={`font-semibold ${c.hasLTD ? 'text-blue-300 text-lg' : 'text-gray-300'}`}>{c.name}</span>
                      {c.hasLTD && <Badge className="ml-2 bg-blue-500/20 text-blue-400 text-xs">That's us</Badge>}
                    </td>
                    <td className={`py-4 px-4 ${c.hasLTD ? 'text-green-400 font-bold' : 'text-gray-400'}`}>{c.price}</td>
                    <td className={`py-4 px-4 ${c.hasLTD ? 'text-green-400 font-bold' : 'text-gray-500'}`}>{c.annual}</td>
                    <td className="py-4 px-4 text-center">
                      {c.hasLTD
                        ? <span className="text-green-400 text-xl">✓</span>
                        : <span className="text-red-500 text-xl">✗</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-center text-gray-500 text-sm mt-4">Competitor pricing as of 2026. Annual costs shown for reference.</p>
        </div>

        {/* ── AI QUALITY COUNCIL (MOA) ── */}
        <div className="mb-24">
          <div className="text-center mb-12">
            <Badge className="bg-purple-500/20 text-purple-400 mb-4 px-4 py-1.5">
              <Brain className="w-3 h-3 mr-2" />
              Exclusive to globalnow.biz
            </Badge>
            <h2 className="text-4xl font-bold text-white mb-4">
              The AI Quality Council
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Every website goes through 7 independent AI reviewers before you see it. No other website builder does this. Most use a single AI model — we use seven, each checking the others' work.
            </p>
          </div>

          {/* Center hub with agents */}
          <div className="relative max-w-4xl mx-auto">
            {/* Hub */}
            <div className="flex justify-center mb-8">
              <div className="bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl p-6 text-center w-48 shadow-2xl shadow-blue-500/30">
                <div className="text-4xl mb-2">🧠</div>
                <div className="text-white font-bold text-lg">Your Website</div>
                <div className="text-blue-200 text-xs mt-1">Reviewed 7 times</div>
              </div>
            </div>

            {/* Agents grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {agents.map((agent, i) => (
                <div
                  key={i}
                  className="bg-gray-900/80 border border-gray-700 hover:border-blue-500/50 rounded-xl p-5 transition-all duration-300 hover:bg-gray-800/80 hover:-translate-y-1"
                  style={{ borderLeftColor: agent.color, borderLeftWidth: '3px' }}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-2xl">{agent.icon}</span>
                    <span className="font-semibold text-white text-sm">{agent.name}</span>
                  </div>
                  <p className="text-gray-400 text-xs leading-relaxed">{agent.role}</p>
                </div>
              ))}
              {/* Quality score card */}
              <div className="bg-gradient-to-br from-green-900/40 to-blue-900/40 border border-green-500/30 rounded-xl p-5 flex flex-col justify-center items-center text-center">
                <div className="text-3xl mb-2">🏆</div>
                <div className="text-green-400 font-bold text-sm">Quality Score</div>
                <div className="text-white text-2xl font-bold mt-1">9.2 / 10</div>
                <div className="text-gray-400 text-xs mt-1">Avg across all generated sites</div>
              </div>
            </div>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto text-center">
            {[
              { icon: '⚡', label: 'Single AI model', sub: 'What competitors use', bad: true },
              { icon: '🤝', label: '7 AI agents', sub: 'Each reviewing the others', bad: false },
              { icon: '✅', label: 'Quality-scored output', sub: 'Before you see a single line', bad: false },
            ].map((item, i) => (
              <div key={i} className={`rounded-xl p-5 border ${item.bad ? 'border-gray-700 bg-gray-900/40' : 'border-blue-500/30 bg-blue-950/30'}`}>
                <div className="text-2xl mb-2">{item.icon}</div>
                <div className={`font-semibold ${item.bad ? 'text-gray-400' : 'text-white'}`}>{item.label}</div>
                <div className="text-gray-500 text-sm">{item.sub}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── WEBSITE GENERATOR FEATURE ── */}
        <div className="mb-24">
          <div className="bg-gradient-to-r from-blue-950/60 to-purple-950/60 border border-blue-500/20 rounded-2xl p-10 md:p-16">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <Badge className="bg-blue-500/20 text-blue-400 mb-4">Core Feature</Badge>
                <h2 className="text-3xl font-bold text-white mb-4">AI Website Generator</h2>
                <p className="text-gray-400 leading-relaxed mb-6">
                  Enter your business name, industry, and a short description. In under 60 seconds, your complete business website is built, reviewed by 7 AIs, and ready to go live — with professional copy, industry photos, Google Maps, and a working contact form already wired in.
                </p>
                <ul className="space-y-3">
                  {[
                    '10 industries × 10 color schemes — 100 combinations',
                    'Professional photos via Pexels, auto-matched to your industry',
                    'Google Maps embed + working contact form included',
                    'One-click deploy to Netlify — live URL in 30 seconds',
                    'Download as ZIP — own your code, no vendor lock-in',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-300 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="space-y-4">
                {[
                  { step: '1', label: 'Enter your details', desc: 'Business name, industry, description' },
                  { step: '2', label: '7 AIs build & review', desc: 'Industry research, copy, design, SEO check' },
                  { step: '3', label: 'Preview your site', desc: 'Live preview in browser, download ZIP' },
                  { step: '4', label: 'Deploy in one click', desc: 'Live on Netlify with a real URL' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-4 bg-gray-900/60 rounded-xl px-5 py-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">{item.step}</div>
                    <div>
                      <div className="text-white font-medium text-sm">{item.label}</div>
                      <div className="text-gray-500 text-xs">{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── CONTENT STUDIO ── */}
        <div className="mb-24">
          <div className="text-center mb-12">
            <Badge className="bg-green-500/20 text-green-400 mb-4 px-4 py-1.5">
              <Zap className="w-3 h-3 mr-2" />
              Content Automation Studio
            </Badge>
            <h2 className="text-3xl font-bold text-white mb-3">Keep Your Business Visible After Launch</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">A website is just the start. Generate blog posts, video scripts, and social media content to stay in front of your customers — no writer needed.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {contentTools.map((tool, i) => (
              <Card key={i} className="bg-gray-800/50 border-gray-700 p-8 hover:bg-gray-800/80 transition-all duration-300 hover:-translate-y-1">
                <div className={`w-12 h-12 bg-${tool.color}-500/20 rounded-xl flex items-center justify-center mb-5`}>
                  <tool.icon className={`w-7 h-7 text-${tool.color}-400`} />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{tool.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{tool.desc}</p>
              </Card>
            ))}
          </div>
        </div>

        {/* ── USE CASES ── */}
        <div className="mb-24">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-3">Built for Anyone Who Needs a Professional Web Presence</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">Whether you're launching your first business or building sites for clients, globalnow.biz handles the heavy lifting.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {useCases.map((useCase, i) => (
              <Card key={i} className="bg-gray-800/50 border-gray-700 p-6 hover:bg-gray-800/80 hover:-translate-y-1 transition-all duration-300 relative">
                {useCase.highlight && (
                  <Badge className="absolute -top-2 right-4 bg-blue-600 text-white text-xs">{useCase.highlight}</Badge>
                )}
                <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center mb-4">
                  <useCase.icon className="w-6 h-6 text-blue-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-3">{useCase.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{useCase.desc}</p>
              </Card>
            ))}
          </div>
        </div>

        {/* ── WHY CHOOSE ── */}
        <div className="mb-24">
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 border border-gray-700 rounded-2xl p-12">
            <h2 className="text-3xl font-bold text-white text-center mb-10">Everything You Need. Nothing You Don't.</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: Crown, text: 'One-time payment — no monthly subscription, ever', color: 'yellow' },
                { icon: Brain, text: '7 AI agents review every website before you see it', color: 'purple' },
                { icon: Clock, text: 'Website live on the internet in under 60 seconds', color: 'blue' },
                { icon: Shield, text: 'Download your code — you own it, no lock-in', color: 'green' },
                { icon: Sparkles, text: 'No coding, no design skills, no technical knowledge needed', color: 'pink' },
                { icon: Users, text: 'Freelancers: one license, unlimited client projects', color: 'orange' },
                { icon: Globe, text: 'Real Netlify deployment — a live URL you can share', color: 'blue' },
                { icon: TrendingUp, text: 'SEO-structured output with meta tags and proper headings', color: 'green' },
                { icon: MessageSquare, text: 'Contact form wired in every generated website', color: 'purple' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className={`bg-${item.color}-500/20 p-2 rounded-lg flex-shrink-0`}>
                    <item.icon className={`w-5 h-5 text-${item.color}-400`} />
                  </div>
                  <span className="text-gray-300 text-sm leading-relaxed">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── HOW IT WORKS ── */}
        <div className="mb-24">
          <h2 className="text-3xl font-bold text-white text-center mb-12">From Sign-Up to Live Website in 3 Steps</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
            {[
              {
                step: 1,
                title: 'Create a Free Account',
                desc: 'Sign up in 30 seconds. No credit card required. Start with the free website generator immediately.',
                cta: null
              },
              {
                step: 2,
                title: 'Generate & Preview',
                desc: 'Enter your business details. 7 AIs build and review your site. Preview it live in your browser.',
                cta: null
              },
              {
                step: 3,
                title: 'Self-service PRO Upgrade',
                desc: `Free tier includes preview and download. Self-service PRO costs ${formatPrice(PRICING.PRO_PRICE)} one-time to deploy live and unlock all content tools.`,
                cta: null
              }
            ].map((item, idx) => (
              <div key={idx} className="relative text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center text-2xl font-bold text-white mx-auto mb-5 shadow-lg shadow-blue-500/30">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{item.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                {idx < 2 && (
                  <div className="hidden md:block absolute top-8 right-0 translate-x-1/2 text-gray-600">
                    <ArrowRight className="w-6 h-6" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ── CTA ── */}
        <div className="text-center">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-12 md:p-16 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-600/50 to-purple-800/50" />
            <div className="relative z-10">
              <h2 className="text-4xl font-bold text-white mb-4">
                Want Your Website Built for You?
              </h2>
              <p className="text-blue-100 text-lg mb-2 max-w-xl mx-auto">
                Get a free sample, then pay ${OFFER.price} one-time after approval. We build and launch your business website.
              </p>
              <p className="text-blue-200 text-sm mb-10 font-medium">
                No account or PRO upgrade required. No monthly fees.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <Link to="/order">
                  <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 text-lg px-10 py-6 font-bold shadow-xl">
                    Get My ${OFFER.price} Website
                  </Button>
                </Link>
                <Link to="/upgrade">
                  <Button size="lg" variant="outline" className="border-white/50 text-white hover:bg-white/10 text-lg px-8 py-6">
                    View Self-service PRO
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Features;
